import React, { FC } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { StepDots } from "../components/StepDots";
import { ContentTypeCard } from "../components/ContentTypeCard";
import { CONTENT_TYPE_CONFIG } from "../../../config/contentTypeConfig";

interface ContentType {
    id: number;
    name: string;
    [key: string]: any;
}

interface ContentTypeStepProps {
    contentTypes: ContentType[];
    contentTypesLoading: boolean;
    selectedContentTypeId: number | string | null;
    onSelect: (contentType: ContentType) => void;
    onBack: () => void;
    onNext: () => void;
}

export const ContentTypeStep: FC<ContentTypeStepProps> = ({
    contentTypes,
    contentTypesLoading,
    selectedContentTypeId,
    onSelect,
    onBack,
    onNext,
}) => {
    return (
        <>
            {/* Body */}
            <Box
                sx={{
                    flex: 1,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    p: "18px 28px 14px",
                    minHeight: 0,
                }}
            >
                <Box mb="10px">
                    <Box mb="6px">
                        <StepDots step={3} />
                    </Box>
                    <Typography
                        sx={{
                            fontSize: 21,
                            fontWeight: 700,
                            color: "var(--text)",
                            letterSpacing: -0.4,
                            fontFamily: "CircularStd",
                        }}
                    >
                        What type of content is this?
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "var(--text-2)", mt: "3px", fontFamily: "CircularStd" }}>
                        Hover a card to preview an example.{" "}
                        {contentTypes.some((ct) => !CONTENT_TYPE_CONFIG[ct.name]) && (
                            <>
                                <Box
                                    component="strong"
                                    sx={{ color: "var(--accent-text)", fontWeight: 600 }}
                                >
                                    NEW
                                </Box>{" "}
                                formats don't have a sample yet — your producer can walk you through them.
                            </>
                        )}
                    </Typography>
                </Box>

                {/* Card grid */}
                {contentTypesLoading ? (
                    <Box
                        flex={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexDirection="column"
                        gap="12px"
                    >
                        <CircularProgress size={28} sx={{ color: "var(--accent)" }} />
                        <Typography sx={{ fontSize: 13, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                            Loading content types…
                        </Typography>
                    </Box>
                ) : contentTypes.length === 0 ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        flex={1}
                        gap="8px"
                    >
                        <CategoryOutlinedIcon sx={{ fontSize: 36, color: "var(--muted)" }} />
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                            No content types configured for this brand yet.
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                            Contact your producer if this looks unexpected.
                        </Typography>
                    </Box>
                ) : (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
                        gridAutoRows: "max-content",
                        alignContent: "start",
                        alignItems: "start",
                        rowGap: "16px",
                        columnGap: "12px",
                        overflowY: "auto",
                        pb: "4px",
                    }}
                >
                    {[...contentTypes]
                        .sort((a, b) =>
                            (CONTENT_TYPE_CONFIG[a.name]?.order ?? 999) -
                            (CONTENT_TYPE_CONFIG[b.name]?.order ?? 999)
                        )
                        .map((ct) => (
                        <ContentTypeCard
                            key={ct.id}
                            contentType={ct}
                            selected={selectedContentTypeId === ct.id || selectedContentTypeId === String(ct.id)}
                            onClick={() => onSelect(ct)}
                        />
                    ))}
                </Box>
                )}
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: "14px 28px",
                    borderTop: "1px solid var(--border)",
                    background: "var(--surface)",
                    flexShrink: 0,
                }}
            >
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    sx={{ textTransform: "capitalize", fontFamily: "CircularStd", color: "var(--text)" }}
                >
                    Back
                </Button>

                {selectedContentTypeId ? (
                    <Button
                        variant="contained"
                        color="primary"
                        endIcon={<ArrowForwardIcon />}
                        onClick={onNext}
                        sx={{
                            textTransform: "capitalize",
                            fontFamily: "CircularStd",
                            borderRadius: "24px",
                            px: 3,
                        }}
                    >
                        Next
                    </Button>
                ) : (
                    <Typography sx={{ fontSize: 12, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                        Click a card to select
                    </Typography>
                )}
            </Box>
        </>
    );
};
